/**
 * Unwrap the `data` field of a Pharos API response, throwing a descriptive error
 * when the response contains no data.
 *
 * Centralises the repeated `if (!response.data) throw new Error(...)` pattern
 * found in every API factory method.
 *
 * @param response - A Pharos API envelope or any object with an optional `data` field.
 * @param context - Optional label (e.g. the calling method name) appended to the error message.
 * @returns The unwrapped data value (never null / undefined).
 * @throws Error when `data` is null or undefined.
 *
 * @example
 * const order = unwrapApiData(
 *   await client.execute<PharosApiResponse<Order>>(() => client.instance.get(`/orders/${id}`)),
 *   'getOrder',
 * );
 */
export function unwrapApiData<T>(response: { data?: T }, context?: string): T {
  if (response.data == null) {
    throw new Error(`Unexpected empty response from API${context ? ` (${context})` : ''}`);
  }
  return response.data;
}
